import { useState } from "react";

import { Button } from "@/components/field";
import {
	type ReverifyTotals,
	useReverify,
} from "@/features/plausibility/use-reverify";

/**
 * Re-verify the alerts the old keyword table down-ranked from Stage 1's words
 * alone, before every alert was checked against its footage. Each goes back to
 * its original severity and wording, then through the verifier like any other
 * alert. No notifications are sent.
 */
export const ReverifyPanel = () => {
	const [progress, setProgress] = useState<null | ReverifyTotals>(null);
	const reverify = useReverify(setProgress);
	const totals = reverify.isPending ? progress : (reverify.data ?? progress);
	const previewed = reverify.data?.dryRun && reverify.data.remaining > 0;

	return (
		<section className="border-secondary rounded-md border p-4">
			<h3 className="text-primary text-sm font-semibold">
				Re-verify text-only down-ranks
			</h3>
			<p className="text-secondary text-sm">
				Alerts that were lowered from Stage 1's words alone, without the footage
				being reviewed. Each is put back to its original severity and wording,
				then checked against its footage. Customers are not notified.
			</p>
			<div className="mt-3 flex flex-wrap items-center gap-2">
				<Button
					disabled={reverify.isPending}
					onClick={() => {
						setProgress(null);
						reverify.mutate({ dryRun: true });
					}}
					type="button"
					variant="secondary"
				>
					Preview (dry run)
				</Button>
				<Button
					disabled={reverify.isPending || !previewed}
					onClick={() => {
						if (
							window.confirm(
								`Re-verify ${reverify.data?.remaining ?? 0} alerts? Their severity and wording go back to what Stage 1 said until the footage check lands.`,
							)
						) {
							setProgress(null);
							reverify.mutate({ dryRun: false });
						}
					}}
					type="button"
				>
					{reverify.isPending && totals && !totals.dryRun
						? "Re-verifying…"
						: "Re-verify"}
				</Button>
			</div>
			{totals && (
				<p className="text-secondary mt-2 text-sm" role="status">
					{totals.dryRun
						? `${totals.remaining} alerts were down-ranked without a footage review.`
						: `Queued ${totals.enqueued} for a footage check${
								totals.failed > 0 ? `, ${totals.failed} failed` : ""
							}; ${totals.remaining} remaining.`}
				</p>
			)}
			{reverify.error && (
				<p className="text-error-primary mt-2 text-sm" role="alert">
					{reverify.error.message}
				</p>
			)}
		</section>
	);
};
